// @ts-nocheck
// =============================================================================
//  KitchenControl — ModuleWelkom
//  Welkomstvenster per module voor nieuwe gebruikers. Verschijnt automatisch de
//  eerste keer dat een module geopend wordt, en is daarna terug te halen via de
//  WelkomKnop (?-knopje). Of het al getoond is, wordt onthouden in localStorage
//  (kc-welkom-*, valt onder de kc-prefix → wist mee bij tenantwissel).
//
//  ALLE TEKSTEN STAAN HIERONDER in het WELKOM-object — pas ze hier aan, op één
//  plek. Per module: titel, intro, kleur, icoon en (max) vier punten.
//
//  Gebruik in een module:
//    import { ModuleWelkom, WelkomKnop } from "./ModuleWelkom";
//    // ergens in de header:           <WelkomKnop module="mep" />
//    // ergens in de render (1x):      <ModuleWelkom module="mep" />
// =============================================================================
import { useState, useEffect } from "react";
import {
  ClipboardList, ShieldCheck, BookOpen, Users, X, HelpCircle,
  SquareCheck, Building2, FileText, Printer, Thermometer, PackageCheck,
  Snowflake, Sparkles, PlusCircle, ClipboardPaste, FolderCog, Clock,
  CalendarClock, TriangleAlert,
} from "lucide-react";

// ── Kleurthema's per module ───────────────────────────────────────────────────
// Elk thema: bg (zacht vlak), bd (rand), txt (donkere tekst), icon (accent).
const THEMA = {
  blauw:   { bg: "#EFF6FF", bd: "#BFDBFE", txt: "#1E3A8A", icon: "#2563EB" },
  groen:   { bg: "#ECFDF5", bd: "#A7F3D0", txt: "#065F46", icon: "#059669" },
  paars:   { bg: "#F5F3FF", bd: "#DDD6FE", txt: "#5B21B6", icon: "#7C3AED" },
  amber:   { bg: "#FFFBEB", bd: "#FDE68A", txt: "#92400E", icon: "#D97706" },
  rose:    { bg: "#FFF1F2", bd: "#FECDD3", txt: "#9F1239", icon: "#E11D48" },
};

// ── DE TEKSTEN — pas hier aan ─────────────────────────────────────────────────
// Per module: Icon (kop-icoon), thema (kleur), titel, intro, en punten[]
// (elk punt: Icon, titel, tekst). Houd het op max 4 punten.
const WELKOM = {
  mep: {
    Icon: ClipboardList,
    thema: "blauw",
    titel: "Welkom bij Mise en Place",
    intro: "Hier plan je per dag en afdeling wat er klaargemaakt moet worden. Zo weet je team elke dienst precies wat er te doen staat.",
    punten: [
      { Icon: SquareCheck, titel: "Aanvinken om te plannen", tekst: "Eén tik plant een item in (oranje), nog een tik zet het op klaar (groen)." },
      { Icon: Building2,   titel: "Afdelingen en categorieën", tekst: "Orden je werk per afdeling (Warme kant, Garde) en categorie (Vlees, Soepen)." },
      { Icon: FileText,    titel: "Bestelartikelen op Whiteboard", tekst: "Bestelartikelen van geplande items verschijnen automatisch op het Whiteboard." },
      { Icon: Printer,     titel: "Etiketten printen", tekst: "Print THT- en allergenenetiketten rechtstreeks vanaf een item." },
    ],
  },

  haccp: {
    Icon: ShieldCheck,
    thema: "groen",
    titel: "Welkom bij HACCP",
    intro: "Leg je voedselveiligheid digitaal vast — temperaturen, ontvangst, schoonmaak en meer. Altijd terug te vinden, klaar voor controle.",
    punten: [
      { Icon: Thermometer,  titel: "Temperaturen registreren", tekst: "Noteer metingen van koelingen, vriezers en gerechten met datum en tijd." },
      { Icon: PackageCheck, titel: "Ontvangst controleren", tekst: "Leg goederenontvangst vast en houd je leveringen bij." },
      { Icon: Snowflake,    titel: "Terugkoelen volgen", tekst: "Volg het terugkoelen van gerechten binnen de NVWA-norm." },
      { Icon: Sparkles,     titel: "Schoonmaak afvinken", tekst: "Registreer je schoonmaaktaken en hygiënecontroles." },
    ],
  },

  recepten: {
    Icon: BookOpen,
    thema: "paars",
    titel: "Welkom bij Recepten",
    intro: "Beheer al je recepten op één plek — met ingrediënten, bereiding en allergenen. Koppel ze aan je mep-items en etiketten.",
    punten: [
      { Icon: BookOpen,       titel: "Recepten bewaren", tekst: "Bewaar recepten met ingrediënten, bereiding en allergenen." },
      { Icon: PlusCircle,     titel: "Nieuw recept", tekst: "Maak een recept aan en vul stap voor stap de gegevens in." },
      { Icon: ClipboardPaste, titel: "Recept plakken", tekst: "Plak een recept als tekst — wij maken er automatisch een net recept van." },
      { Icon: FolderCog,      titel: "Categorieën", tekst: "Deel je recepten in categorieën in voor een snel overzicht." },
    ],
  },

  team: {
    Icon: Users,
    thema: "amber",
    titel: "Welkom bij Team",
    intro: "Beheer je personeel en het weekrooster. Handig voor de planning én voor de naam op je etiketten.",
    punten: [
      { Icon: Users,        titel: "Medewerkers beheren", tekst: "Houd naam, telefoon, e-mail en functie van je team bij." },
      { Icon: Clock,        titel: "Weekrooster invullen", tekst: "Vul begin- en eindtijden per medewerker in." },
      { Icon: CalendarClock, titel: "Overzicht per week", tekst: "Zie in één oogopslag wie wanneer werkt." },
      { Icon: Printer,      titel: "Naam op etiketten", tekst: "Kies bij het printen wie het gerecht heeft bereid." },
    ],
  },
};

const SLEUTEL = (mod) => `kc-welkom-${mod}`;

function isGezien(mod) {
  try { return localStorage.getItem(SLEUTEL(mod)) === "1"; } catch { return false; }
}
function zetGezien(mod) {
  try { localStorage.setItem(SLEUTEL(mod), "1"); } catch {}
}

// Alle welkomstvensters weer laten verschijnen (handig tijdens testen).
export function resetWelkom() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith("kc-welkom-")) localStorage.removeItem(k);
    }
  } catch {}
}

// ── Het ?-knopje om het venster opnieuw te openen ─────────────────────────────
export function WelkomKnop({ module, style }) {
  const conf = WELKOM[module];
  const thema = THEMA[conf?.thema] || THEMA.blauw;
  if (!conf) return null;
  return (
    <button
      onClick={() => { try { window.dispatchEvent(new CustomEvent(`kc-welkom-open-${module}`)); } catch {} }}
      title="Uitleg over deze module"
      aria-label="Uitleg over deze module"
      style={{ background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.2)", color: "#fff", borderRadius: 8, cursor: "pointer", padding: "6px 9px", display: "flex", alignItems: "center", ...style }}>
      <HelpCircle size={15} />
    </button>
  );
}

// ── Het welkomstvenster zelf ──────────────────────────────────────────────────
export function ModuleWelkom({ module }) {
  const conf = WELKOM[module];
  const thema = THEMA[conf?.thema] || THEMA.blauw;

  // Open bij eerste bezoek (nog niet gezien), of wanneer de WelkomKnop een
  // open-event stuurt. nietMeer = vinkje "niet meer tonen".
  const [open, setOpen] = useState(() => conf ? !isGezien(module) : false);
  const [nietMeer, setNietMeer] = useState(true);

  useEffect(() => {
    const openHandler = () => { setNietMeer(isGezien(module)); setOpen(true); };
    const naam = `kc-welkom-open-${module}`;
    window.addEventListener(naam, openHandler);
    return () => window.removeEventListener(naam, openHandler);
  }, [module]);

  // Escape sluit
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && sluit();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  if (!conf || !open) return null;

  const sluit = () => {
    if (nietMeer) zetGezien(module);
    setOpen(false);
  };

  const KopIcon = conf.Icon;

  return (
    <div onClick={(e) => e.target === e.currentTarget && sluit()}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 800, padding: 16, fontFamily: "system-ui,-apple-system,sans-serif" }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 440, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 70px rgba(0,0,0,.3)" }}>

        {/* Kop */}
        <div style={{ padding: "22px 22px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: thema.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {KopIcon && <KopIcon size={24} color={thema.icon} />}
            </div>
            <h2 style={{ fontSize: 19, fontWeight: 700, color: "#0F172A", margin: 0, flex: 1 }}>{conf.titel}</h2>
            <button onClick={sluit} aria-label="Sluiten" style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: 4, display: "flex", flexShrink: 0 }}><X size={18} /></button>
          </div>
          <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.6, margin: "0 0 18px" }}>{conf.intro}</p>
        </div>

        {/* Punten */}
        <div style={{ padding: "0 22px" }}>
          {(conf.punten || []).slice(0, 4).map((p, i) => {
            const PIcon = p.Icon;
            return (
              <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderTop: "1px solid #F1F5F9" }}>
                {PIcon && <PIcon size={20} color={thema.icon} style={{ flexShrink: 0, marginTop: 1 }} />}
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0F172A", marginBottom: 1 }}>{p.titel}</div>
                  <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.5 }}>{p.tekst}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Voet */}
        <div style={{ padding: "18px 22px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderTop: "1px solid #F1F5F9", marginTop: 4 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#64748B", cursor: "pointer" }}>
            <input type="checkbox" checked={nietMeer} onChange={(e) => setNietMeer(e.target.checked)} style={{ width: 15, height: 15, accentColor: thema.icon, cursor: "pointer" }} />
            Niet meer tonen
          </label>
          <button onClick={sluit} style={{ background: thema.icon, color: "#fff", border: "none", borderRadius: 9, padding: "9px 20px", fontSize: 14, fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
            Aan de slag
          </button>
        </div>

      </div>
    </div>
  );
}

export default ModuleWelkom;

